import { Box } from '@mui/system';
import { sentenceCase } from 'change-case';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
// material
import { Card, Checkbox, Container, IconButton, Stack, tableCellClasses } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDispatch } from 'react-redux';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
// components
import BrudCrumbs from '../../components/BreadCrumbs';
import Label from '../../components/Label';
import Page from '../../components/Page';
import PageTitle from '../../components/PageHeading';
import Scrollbar from '../../components/Scrollbar';
import SearchNotFound from '../../components/SearchNotFound';
import { UserListHead, UserMoreMenu } from '../../components/_dashboard/user';
import { fDate, fDateTime2 } from '../../utils/formatTime';
import DisburseToolbar from './DisburseToolbar';
import { NOT_AVAILABLE } from '../Global/Constants';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import { getDisburseList } from '../../store/Slice/Disburse/DisburseSlice';
import useDisburseSelector from '../../store/Slice/Disburse/DisburseSelector';
import { isValidValue, normalizeValue } from '../../utils/CommonFunction';
import SimplePagination from '../../components/SimplePagination';
import useDebounce from '../../utils/useDebounce';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'id', label: 'APPLICATION ID', alignRight: false },
  { id: 'lanId', label: 'Lan Id', alignRight: false },
  { id: 'created', label: 'Submission Date', alignRight: false },
  { id: 'name', label: 'NAME', alignRight: false },
  { id: 'companyName', label: 'Company Name', alignRight: false },
  { id: 'contactNumber', label: 'Contact Number', alignRight: false },
  { id: 'sourceFlag', label: 'File Source', alignRight: false },
  { id: 'loanType', label: 'Product', alignRight: false },
  { id: 'city', label: 'City', alignRight: false },
  { id: 'amount', label: 'Amount', alignRight: false },
  { id: 'bankName', label: 'Bank', alignRight: false },
  { id: 'dsaName', label: 'DSA Name', alignRight: false },
  { id: 'status', label: 'status', alignRight: false },
  { id: 'created', label: 'Last update', alignRight: false },
  { id: 'action', label: 'ACTION' }
];

// ----------------------------------------------------------------------

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0
  }
}));
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    fontSize: 12,
    padding: '6px',
    height: '42.5px',
    fontWeight: 500,
    textTransform: 'uppercase',
    color: '#0d4689'
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 11,
    padding: '4px 6px',
    height: '42.5px'
  }
}));

function Disburse() {
  const [selected, setSelected] = useState([]);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState();
  const [filterName, setFilterName] = useState('');
  const [filterState, setFilterState] = useState({
    bankId: null,
    loanTypeId: null,
    cityId: null,
    city: null,
    statusName: ' ',
    statusId: null
  });
  const [filterDatesRange, setFilterDatesRange] = useState([
    {
      startDate: null,
      endDate: null,
      key: 'selection'
    }
  ]);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [hasNextPage, setHasNextPage] = useState(true);
  const debouncedSearch = useDebounce(filterName, 600);

  const dispatch = useDispatch();
  const { LoginData } = useLoginSelector();
  const { DisburseListData } = useDisburseSelector();

  useEffect(() => {
    const selectedItem = localStorage.getItem('selectedItem');
    if (selectedItem !== null) {
      localStorage.removeItem('selectedItem');
    }
  }, []);

  const getDisburseListApi = useCallback(() => {
    const credentials = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId,
      accessLevelId: LoginData?.accessLevelId,
      stageId: 58,
      filterModel: {
        bankId: filterState.bankId || null,
        loanTypeId: filterState.loanTypeId || null,
        cityId: filterState.cityId || null,
        disburseStatusName: filterState.statusId ? filterState.statusName : null
      },
      entityListRequestModel: {
        searchLiteral: debouncedSearch?.length >= 2 ? debouncedSearch : null,
        pageSize: rowsPerPage,
        pageNumber: page + 1,
        sortOrder: order || 'desc'
      }
    };
    dispatch(getDisburseList(credentials));
  }, [LoginData, filterState, debouncedSearch, order, dispatch, page, rowsPerPage]);

  useEffect(() => {
    getDisburseListApi();
  }, [getDisburseListApi]);

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };
  const handleRequestSort = (event, property) => {
    if (property !== 'action') {
      setOrder(orderBy === property && order === 'asc' ? 'desc' : 'asc');
      setOrderBy(property);
    }
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const list = DisburseListData.filter(
        (item, index) =>
          item.status?.toLowerCase() === 'disbursed' || item.status?.toLowerCase() === 'u-disbursed'
      );
      const newSelecteds = list.map((n) => n);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, item) => {
    const selectedIndex = selected.indexOf(item);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, item);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  useEffect(() => {
    if (!DisburseListData) {
      setHasNextPage(true);
      return;
    }
    // If API returned fewer rows → last page
    setHasNextPage(DisburseListData.length >= rowsPerPage);
  }, [DisburseListData, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch]);

  return (
    <Page title="Partner Management System || Disburse">
      <Container maxWidth="xl">
        <Box pt={3} pb={3} mb={4}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="pageHeading"
          >
            <PageTitle info="Disburse" />
            <Box>
              <BrudCrumbs />
            </Box>
          </Stack>
          <Card>
            <DisburseToolbar
              numSelected={selected.length}
              filterName={filterName}
              setFilterName={setFilterName}
              onFilterName={handleFilterByName}
              filterDatesRange={filterDatesRange}
              setFilterDatesRange={setFilterDatesRange}
              filterState={filterState}
              setFilterState={setFilterState}
              parent="disburse"
              selected={selected}
            />

            <Scrollbar>
              <TableContainer sx={{ minWidth: 800, minHeight: '50vh', overflow: 'auto' }}>
                <Table>
                  <UserListHead
                    order={order}
                    orderBy={orderBy}
                    headLabel={TABLE_HEAD}
                    rowCount={DisburseListData?.length}
                    numSelected={selected.length}
                    onRequestSort={handleRequestSort}
                    onSelectAllClick={handleSelectAllClick}
                    isCheckBox="true"
                  />
                  <TableBody>
                    {DisburseListData?.map((item, index) => {
                      const isItemSelected = selected.indexOf(item) !== -1;
                      return (
                        <StyledTableRow
                          hover
                          key={item.applicationId}
                          tabIndex={-1}
                          role="checkbox"
                          selected={isItemSelected}
                          aria-checked={isItemSelected}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              className={
                                item.status?.toLowerCase() === 'disbursed' ||
                                item.status?.toLowerCase() === 'u-disbursed'
                                  ? 'enabledDocument'
                                  : 'disabledDocument'
                              }
                              checked={isItemSelected}
                              onChange={(event) => handleClick(event, item)}
                            />
                          </TableCell>
                          <StyledTableCell align="left">
                            {normalizeValue(item?.applicationId, NOT_AVAILABLE)}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            {normalizeValue(item?.lanId, NOT_AVAILABLE)}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            {normalizeValue(
                              item?.submissionDate ? fDate(item.submissionDate) : null,
                              NOT_AVAILABLE
                            )}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            {normalizeValue(item?.customerName, NOT_AVAILABLE)}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            {normalizeValue(item?.companyName, NOT_AVAILABLE)}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            {normalizeValue(item?.contactNumber, NOT_AVAILABLE)}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            <Label
                              variant="ghost"
                              color={
                                item?.sourceFlag === 'LOGIN_FILE' ||
                                item?.logInStatusName === 'Rejected'
                                  ? 'warning'
                                  : 'primary'
                              }
                              sx={{
                                opacity: 0.8,
                                fontWeight: 500
                              }}
                            >
                              {normalizeValue(item?.sourceFlag, NOT_AVAILABLE)}
                            </Label>
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            {normalizeValue(item?.loanType, NOT_AVAILABLE)}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            {normalizeValue(item?.cityName, NOT_AVAILABLE)}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            {normalizeValue(item?.loanAmount, NOT_AVAILABLE)}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            {normalizeValue(item?.bankName, NOT_AVAILABLE)}
                          </StyledTableCell>

                          <StyledTableCell align="left">
                            {normalizeValue(item?.dsaName, NOT_AVAILABLE)}
                          </StyledTableCell>

                          <StyledTableCell align="left">
                            <Label
                              variant="ghost"
                              color={
                                item?.status === 'Closed' || item?.status === 'Rejected'
                                  ? 'warning'
                                  : 'success'
                              }
                            >
                              {isValidValue(item?.status)
                                ? sentenceCase(item?.status)
                                : NOT_AVAILABLE}
                            </Label>
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            {isValidValue(item?.lastUpdatedDate)
                              ? fDate(item.lastUpdatedDate)
                              : NOT_AVAILABLE}
                          </StyledTableCell>
                          <StyledTableCell align="" width={115}>
                            <UserMoreMenu rowId={item} parent="disburse" />
                          </StyledTableCell>
                        </StyledTableRow>
                      );
                    })}
                  </TableBody>
                  {DisburseListData?.length === 0 && (
                    <TableBody>
                      <TableRow>
                        <StyledTableCell align="center" colSpan={12} sx={{ py: 3 }}>
                          <SearchNotFound searchQuery={filterName} />
                        </StyledTableCell>
                      </TableRow>
                    </TableBody>
                  )}
                </Table>
              </TableContainer>
            </Scrollbar>
            <SimplePagination page={page} setPage={setPage} hasNextPage={hasNextPage} />
          </Card>
        </Box>
      </Container>
    </Page>
  );
}
export default Disburse;

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ paddingTop: '1rem' }}>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired
};
